import React, { useRef } from "react";
import { motion, useReducedMotion, useMotionValue, useTransform } from "framer-motion";
import { hero } from "../content/landingContent";
import { CtaButton } from "./CtaButton";
import { AssetPlaceholder } from "./AssetPlaceholder";
import { useResourceImage } from "../lib/useResourceImage";
import { useContentField, useLinks } from "../lib/useContent";
import vehiclesComposition from "../assets/hero/vehicles-composition.jpg";
import appMockup from "../assets/app/app-mockup.jpg";
import { ArrowRight, Search } from "lucide-react";
import { trackLead, trackViewContent } from "../lib/metaPixel";

export const Hero: React.FC = () => {
  const reduced = useReducedMotion();
  const wrapRef = useRef<HTMLDivElement>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useTransform(my, [-40, 40], [4, -4]);
  const rotY = useTransform(mx, [-40, 40], [-4, 4]);
  const { src: compositionSrc } = useResourceImage("hero.composition", vehiclesComposition);
  const links = useLinks();
  const eyebrow = useContentField("hero.eyebrow", hero.eyebrow);
  const title = useContentField("hero.title", hero.title);
  const highlight = useContentField("hero.highlight", hero.highlight);
  const description = useContentField("hero.description", hero.description);
  const ctaPrimary = useContentField("hero.ctaPrimary", hero.ctaPrimary);
  const ctaSecondary = useContentField("hero.ctaSecondary", hero.ctaSecondary);
  const titleLines = title.split("\n");

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduced || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    mx.set(e.clientX - rect.left - rect.width / 2);
    my.set(e.clientY - rect.top - rect.height / 2);
  };

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-28 pb-16 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-28"
    >
      <div
        aria-hidden="true"
        className="absolute -top-24 -right-32 h-[420px] w-[420px] rounded-full bg-motor-red/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute top-40 -left-40 h-[320px] w-[320px] rounded-full bg-motor-gold/10 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <div>
          <motion.div
            initial={reduced ? undefined : { opacity: 0, y: 14 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full bg-motor-red/10 border border-motor-red/20 px-4 py-2 text-[11px] sm:text-xs font-display font-semibold uppercase tracking-[0.16em] text-motor-red"
          >
            {eyebrow}
          </motion.div>

          <h1 className="mt-6 font-display font-bold text-motor-ink leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
            {titleLines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={reduced ? undefined : { y: "110%" }}
                  animate={reduced ? undefined : { y: "0%" }}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={reduced ? undefined : { opacity: 0, y: 12 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            data-testid="hero-highlight"
            className="mt-5 font-display font-semibold text-xl sm:text-2xl text-motor-red"
          >
            {highlight}
          </motion.p>

          <motion.p
            initial={reduced ? undefined : { opacity: 0, y: 12 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65 }}
            data-testid="hero-description"
            className="mt-4 text-base text-neutral-600 max-w-lg leading-relaxed"
          >
            {description}
          </motion.p>

          <motion.div
            initial={reduced ? undefined : { opacity: 0, y: 12 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.78 }}
            className="mt-9 flex flex-col sm:flex-row gap-3.5"
          >
            <CtaButton
              href={links.appUrl}
              onClick={() => trackLead("hero_download")}
              size="lg"
              testId="hero-download-app-button"
              icon={<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
            >
              {ctaPrimary}
            </CtaButton>
            <CtaButton
              href={links.exploreUrl}
              onClick={() => trackViewContent("hero_explore")}
              variant="outline"
              size="lg"
              testId="hero-explore-vehicles-button"
              icon={<Search className="h-4 w-4" />}
            >
              {ctaSecondary}
            </CtaButton>
          </motion.div>
        </div>

        <div
          ref={wrapRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => {
            mx.set(0);
            my.set(0);
          }}
          className="relative h-[420px] sm:h-[480px] lg:h-[560px]"
          style={{ perspective: 1000 }}
        >
          <motion.div style={{ rotateX: rotX, rotateY: rotY }} className="relative h-full w-full">
            <div className="absolute inset-0 rounded-[2rem] overflow-hidden shadow-2xl">
              <img
                src={compositionSrc}
                alt="Composición de vehículos disponibles en MotorPRO Autos"
                className="h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-motor-red/30 via-transparent to-transparent" />
            </div>

            <motion.div
              initial={reduced ? undefined : { opacity: 0, y: 20 }}
              animate={reduced ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="absolute -bottom-8 -left-6 sm:-left-10 w-[180px] sm:w-[210px]"
              data-testid="hero-app-mockup-slot"
            >
              <AssetPlaceholder
                resourceKey="hero.appMockup"
                fallbackSrc={appMockup}
                variant="phone"
                label="Vista app"
                alt="Vista previa de la aplicación MotorPRO"
                hint="Espacio editable: mockup de la app"
                className="bg-white shadow-2xl border-motor-red/40"
              />
            </motion.div>

            <div className="absolute -top-5 -right-5 h-16 w-16 rounded-2xl bg-motor-gold/90 shadow-lg" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
